import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const useMenuFilter = (menu = {}) => {

    const navigate = useNavigate();
    const { search } = useParams();

    // State variables
    const [matchingItems, setMatchingItems] = useState([]);
    const [searchKeyword, setSearchKeyword] = useState('');

    // Flatten all menu items
    const allMenuItems = Object.values(menu).flat();

    // Enhanced category display names for better search matching
    const categoryDisplayNames = { 
        appetizersCategory: ["appetizer", "starters", "snacks", "small bites", "appetizers"], 
        soupsSaladsCategory: ["soup", "soups", "salad", "salads", "broth", "greens"], 
        mainCoursesCategory: ["main course", "main courses", "entree", "entrees", "meal", "dinner", "lunch"], 
        dessertsCategory: ["dessert", "desserts", "cake", "cookies", "sweet", "sweets", "pastry", "tart"], 
        beveragesCategory: ["beverage", "drinks", "drink", "beverages", "cocktail", "smoothie", "juice", "coffee", "tea"] 
    };

    useEffect(() => {
        setSearchKeyword(search || '');
    }, [search]);

    useEffect(() => {
        let filteredItems = searchKeyword 
            ? handleHeaderSearch(searchKeyword)
            : [...allMenuItems];

        setMatchingItems(filteredItems);
    }, [searchKeyword, menu]); // Added menu to dependency array

    const handleHeaderSearch = (searchTerm) => {
        const searchLower = searchTerm.toLowerCase().trim();
        
        return allMenuItems.filter((item) => {
            // 1. First find which category this item belongs to
            const itemCategory = Object.keys(menu).find(key => 
                menu[key].includes(item)
            );
            
            // 2. Enhanced category matching
            const isCategoryMatch = 
                itemCategory?.toLowerCase().includes(searchLower.replace(/\s+/g, '')) ||
                itemCategory?.toLowerCase().replace(/\s+/g, '').includes(searchLower.replace(/\s+/g, '')) ||
                (itemCategory && categoryDisplayNames[itemCategory]?.some(term => 
                    term.toLowerCase().includes(searchLower) || 
                    searchLower.includes(term.toLowerCase()))
                );
            
            // 3. Regular item property matching
            const isItemMatch = (
                item.title.toLowerCase().includes(searchLower) || 
                item.content.toLowerCase().includes(searchLower)
            );
            
            return isCategoryMatch || isItemMatch;
        });
    };

    const handleHeaderSearchSubmit = (searchQuery) => {
        const encodedQuery = encodeURIComponent(searchQuery);
        navigate(`/Menu/${encodedQuery}`);
    };

    return {
        searchKeyword,
        matchingItems,
        handleHeaderSearchSubmit,
    };
};

// ================================
// Utility Functions
// ================================

/**
 * Function to filter menu items by category
 */
export const filterMenuByCategory = (menu, category) => {
    return menu[category] || [];
};

/**
 * Function to get menu categories from a menu object.
 */
export const getMenuCategories = (menu) => {
    return Object.entries(menu).map(([key, items], index) => ({
        index, // Position of the category in the array
        category: key, // Category name (key from the menu object)
        image: items[0]?.image || '', // Image from the first item of the category, or empty string if not available
    }));
};

/**
 * Function to format a camelCase or PascalCase variable name into a readable category name.
 */
export const formatCategoryName = (variableName) => {
    const cleaned = variableName.replace(/Category$/, ''); // Remove 'Category' suffix if present
    const withSpaces = cleaned.replace(/([a-z])([A-Z])/g, '$1 $2'); // Add spaces between camelCase words
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1); // Capitalize the first letter
};